// Ad & analytics pixel layer: GTM, GA4, Google Ads (2 accounts), Taboola, Whop.
// All pixels no-op until their IDs are provided in frontend/.env:
//   REACT_APP_GTM_ID                    (e.g. GTM-XXXXXXX)
//   REACT_APP_GA4_MEASUREMENT_ID        (e.g. G-XXXXXXXXXX)
//   REACT_APP_GOOGLE_ADS_ID             (Ads account #1, e.g. AW-XXXXXXXXX)
//   REACT_APP_GOOGLE_ADS_ID_2           (Ads account #2, optional)
//   REACT_APP_GOOGLE_ADS_CONVERSIONS    (comma-separated send_to labels, e.g. "AW-XXX/AbC,AW-YYY/DeF")
//   REACT_APP_GOOGLE_ADS_CONVERSION_LABEL (legacy single label, still honored)
//   REACT_APP_TABOOLA_PIXEL_ID          (numeric Taboola universal pixel id)
//   REACT_APP_WHOP_COMPANY_ID           (e.g. biz_xxxxxxxxxxxxx)

export const PIXEL_CONFIG = {
  GTM_ID: process.env.REACT_APP_GTM_ID || "",
  GA4_MEASUREMENT_ID: process.env.REACT_APP_GA4_MEASUREMENT_ID || "",
  GOOGLE_ADS_ID: process.env.REACT_APP_GOOGLE_ADS_ID || "",
  GOOGLE_ADS_ID_2: process.env.REACT_APP_GOOGLE_ADS_ID_2 || "",
  GOOGLE_ADS_CONVERSION_LABEL:
    process.env.REACT_APP_GOOGLE_ADS_CONVERSION_LABEL || "",
  GOOGLE_ADS_CONVERSIONS: (process.env.REACT_APP_GOOGLE_ADS_CONVERSIONS || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean),
  // Dedicated conversion actions (comma-separated send_to values,
  // e.g. "AW-18414109334/AbCdEfGhIj,AW-17259834340/KlMnOpQrSt")
  GADS_APPLICATION_SUBMIT: (process.env.REACT_APP_GADS_APPLICATION_SUBMIT || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean),
  GADS_CALL_BOOKED: (process.env.REACT_APP_GADS_CALL_BOOKED || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean),
  TABOOLA_PIXEL_ID: process.env.REACT_APP_TABOOLA_PIXEL_ID || "",
  WHOP_COMPANY_ID: process.env.REACT_APP_WHOP_COMPANY_ID || "",
};

let initialized = false;

const loadScript = (src, id) => {
  if (id && document.getElementById(id)) return;
  const s = document.createElement("script");
  s.async = true;
  s.src = src;
  if (id) s.id = id;
  document.head.appendChild(s);
};

/* ---------- Google Tag Manager ---------- */
const initGTM = () => {
  const { GTM_ID } = PIXEL_CONFIG;
  if (!GTM_ID) return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ "gtm.start": +new Date(), event: "gtm.js" });
  loadScript(`https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`, "gtm-js");
};

/* ---------- gtag (GA4 + Google Ads, up to 2 Ads accounts) ---------- */
const initGtag = () => {
  const { GA4_MEASUREMENT_ID, GOOGLE_ADS_ID, GOOGLE_ADS_ID_2 } = PIXEL_CONFIG;
  if (!GA4_MEASUREMENT_ID && !GOOGLE_ADS_ID && !GOOGLE_ADS_ID_2) return;
  const firstId = GA4_MEASUREMENT_ID || GOOGLE_ADS_ID || GOOGLE_ADS_ID_2;
  loadScript(
    `https://www.googletagmanager.com/gtag/js?id=${firstId}`,
    "gtag-js"
  );
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    window.dataLayer.push(arguments);
  };
  // keep gclid alive across pages when cookies are restricted
  window.gtag("set", "url_passthrough", true);
  window.gtag("js", new Date());
  if (GA4_MEASUREMENT_ID) {
    // SPA: page_view sent manually via trackPixelEvent
    window.gtag("config", GA4_MEASUREMENT_ID, { send_page_view: false });
  }
  if (GOOGLE_ADS_ID) {
    window.gtag("config", GOOGLE_ADS_ID, { allow_enhanced_conversions: true });
  }
  if (GOOGLE_ADS_ID_2) {
    window.gtag("config", GOOGLE_ADS_ID_2, {
      allow_enhanced_conversions: true,
    });
  }
};

/* ---------- Taboola universal pixel ---------- */
const initTaboola = () => {
  const { TABOOLA_PIXEL_ID } = PIXEL_CONFIG;
  if (!TABOOLA_PIXEL_ID) return;
  window._tfa = window._tfa || [];
  loadScript(
    `https://cdn.taboola.com/libtrc/unip/${TABOOLA_PIXEL_ID}/tfa.js`,
    "tb_tfa_script"
  );
};

/* ---------- Whop pixel ---------- */
const initWhop = () => {
  const { WHOP_COMPANY_ID } = PIXEL_CONFIG;
  if (!WHOP_COMPANY_ID || window.whop) return;
  // Official Whop pixel stub (queues events until s.js loads)
  const u = "https://t.whop.tw";
  const a = (window.whop = {
    q: [],
    t: +new Date(),
    s: [],
    o: u,
    track: function () {
      a.q.push([+new Date()].concat([].slice.call(arguments)));
    },
    setScope: function () {
      a.s = [].slice.call(arguments).filter(function (x) {
        return typeof x === "string";
      });
      a.q.push([+new Date(), "setScope"].concat(a.s));
    },
    scope: function () {
      const c = [].slice.call(arguments);
      return {
        track: function () {
          a.q.push(
            [+new Date()].concat([].slice.call(arguments)).concat([{ scope: c }])
          );
        },
      };
    },
  });
  loadScript(`${u}/s.js`, "whop-pixel");
  window.whop.setScope(WHOP_COMPANY_ID);
};

/* ---------- Direct Whop event helper ----------
   Fires a Whop pixel event by name (queues until s.js loads). Safe no-op
   if the Whop pixel isn't present. Used for per-step + call_booked events. */
export const whopTrack = (name, data = {}) => {
  try {
    if (window.whop && typeof window.whop.track === "function") {
      window.whop.track(name, data);
    }
  } catch (e) {
    // never break the app because of a pixel
  }
};

export const initPixels = () => {
  if (initialized) return;
  initialized = true;
  try {
    initGTM();
    initGtag();
    initTaboola();
    initWhop();
  } catch (e) {
    // pixels must never break the app
  }
};

/* ---------- Enhanced Conversions: hand Google raw PII (it hashes) ---------- */
export const setUserData = (email, first, last, phone) => {
  try {
    if (!window.gtag) return;
    const data = {};
    if (email) data.email = String(email).trim().toLowerCase();
    if (phone) data.phone_number = String(phone).trim();
    const address = {};
    if (first) address.first_name = String(first).trim().toLowerCase();
    if (last) address.last_name = String(last).trim().toLowerCase();
    if (address.first_name || address.last_name) data.address = address;
    if (data.email || data.phone_number || data.address) {
      window.gtag("set", "user_data", data);
    }
  } catch (e) {
    // never break the app
  }
};

/* ---------- Conversion fan-out (fires once per registration) ----------
   De-duplicated via localStorage so a refresh never double-counts. */
const CONV_KEY = "tmu_conversions_sent";
const alreadySent = (txn) => {
  try {
    const sent = JSON.parse(localStorage.getItem(CONV_KEY) || "[]");
    if (sent.includes(txn)) return true;
    sent.push(txn);
    localStorage.setItem(CONV_KEY, JSON.stringify(sent.slice(-20)));
    return false;
  } catch (e) {
    return false;
  }
};

export const fireRegistrationConversion = ({
  transactionId,
  email,
  firstName,
  lastName,
  phone,
} = {}) => {
  try {
    const txn = transactionId || `reg_${email || Date.now()}`;
    if (alreadySent(txn)) return;

    const {
      GA4_MEASUREMENT_ID,
      GOOGLE_ADS_CONVERSIONS,
      GOOGLE_ADS_ID,
      GOOGLE_ADS_CONVERSION_LABEL,
      TABOOLA_PIXEL_ID,
      WHOP_COMPANY_ID,
    } = PIXEL_CONFIG;

    // Enhanced Conversions first (before firing the conversion)
    setUserData(email, firstName, lastName, phone);

    if (window.dataLayer) {
      window.dataLayer.push({
        event: "webinar_registration",
        transaction_id: txn,
        value: 0,
        currency: "USD",
        email: email,
      });
    }

    if (window.gtag && GA4_MEASUREMENT_ID) {
      window.gtag("event", "webinar_registration", {
        transaction_id: txn,
        value: 0,
        currency: "USD",
        send_to: GA4_MEASUREMENT_ID,
      });
    }

    // One direct Ads conversion per configured label (falls back to legacy)
    if (window.gtag) {
      const labels =
        GOOGLE_ADS_CONVERSIONS.length > 0
          ? GOOGLE_ADS_CONVERSIONS
          : GOOGLE_ADS_ID && GOOGLE_ADS_CONVERSION_LABEL
          ? [`${GOOGLE_ADS_ID}/${GOOGLE_ADS_CONVERSION_LABEL}`]
          : GOOGLE_ADS_ID
          ? [GOOGLE_ADS_ID]
          : [];
      labels.forEach((sendTo) => {
        window.gtag("event", "conversion", {
          send_to: sendTo,
          transaction_id: txn,
          value: 0,
          currency: "USD",
        });
      });
    }

    // Taboola
    if (window._tfa && TABOOLA_PIXEL_ID) {
      window._tfa.push({
        notify: "event",
        name: "web_reg",
        id: Number(TABOOLA_PIXEL_ID),
      });
    }

    // Whop: 3 standard events with a shared event_id (dedupe with S2S)
    if (window.whop && WHOP_COMPANY_ID) {
      ["lead", "complete_registration", "submit_application"].forEach((name) => {
        window.whop.track(name, {
          event_id: `reg_${email || txn}`,
          email: email,
          external_id: txn,
        });
      });
    }
  } catch (e) {
    // never break the app
  }
};

/* ---------- Dedicated Google Ads conversion actions ----------
   Fires one Google Ads "conversion" per configured send_to label, with
   Enhanced Conversions (hashed PII) and de-duplication per event+txn.
   Used for the application-completed and call-booked conversions. */
export const fireGoogleAdsConversion = (
  labels,
  { transactionId, email, firstName, lastName, phone, eventKey, value } = {}
) => {
  try {
    if (!window.gtag || !labels || labels.length === 0) return;
    const txn = transactionId || `conv_${email || Date.now()}`;
    const dedupeKey = `${eventKey || "ads"}:${txn}`;
    if (alreadySent(dedupeKey)) return;
    // Enhanced Conversions first, so Google can match the click to the lead
    setUserData(email, firstName, lastName, phone);
    labels.forEach((sendTo) => {
      window.gtag("event", "conversion", {
        send_to: sendTo,
        transaction_id: txn,
        value: value || 0,
        currency: "USD",
      });
    });
  } catch (e) {
    // never break the app
  }
};

/* ---------- Event forwarding ----------
   Called by analytics.track() so every internal event is mirrored
   to all configured ad platforms with sensible mappings. */
export const trackPixelEvent = (eventType, data = {}) => {
  try {
    const {
      GA4_MEASUREMENT_ID,
      GOOGLE_ADS_ID,
      GOOGLE_ADS_CONVERSION_LABEL,
      TABOOLA_PIXEL_ID,
      WHOP_COMPANY_ID,
    } = PIXEL_CONFIG;

    const gtag = window.gtag;
    const tfa = window._tfa;
    const whop = window.whop;

    // --- GA4: mirror every event ---
    if (gtag && GA4_MEASUREMENT_ID) {
      if (eventType === "page_view") {
        gtag("event", "page_view", {
          page_path: window.location.pathname,
          page_location: window.location.href,
        });
      } else {
        gtag("event", eventType, { ...data });
      }
    }

    // --- Taboola ---
    if (tfa && TABOOLA_PIXEL_ID) {
      const id = Number(TABOOLA_PIXEL_ID);
      if (eventType === "page_view") {
        tfa.push({ notify: "event", name: "page_view", id });
      } else if (eventType === "form_step_view" && data.step === 1) {
        tfa.push({ notify: "event", name: "start_application", id });
      } else if (eventType === "form_submit") {
        tfa.push({ notify: "event", name: "lead", id });
      } else if (eventType === "scroll_depth") {
        tfa.push({ notify: "event", name: `scroll_${data.percent}`, id });
      } else if (eventType === "cta_click") {
        tfa.push({ notify: "event", name: "cta_click", id });
      }
    }

    // --- Whop ---
    if (whop && WHOP_COMPANY_ID) {
      if (eventType === "page_view") {
        whop.track("page");
        // Dedicated event so a Whop Ads audience "ALL PAGE VISITORS" can be
        // built to auto-capture every visitor on any pixel-tagged page.
        whop.track("all_page_visitors");
      } else if (eventType === "scroll_depth") {
        whop.track(`scroll_${data.percent}`);
      } else if (
        eventType === "form_step_complete" &&
        data.step === 3
      ) {
        // phone captured
        whop.track("phone_given");
      }
      // NOTE: registration/lead/complete_registration/submit_application are
      // fired together (with a shared event_id) by fireRegistrationConversion.
    }

    // --- Google Ads generate_lead signal on submit (the actual conversion
    //     fan-out with transaction_id + Enhanced Conversions is handled by
    //     fireRegistrationConversion, called from the form on submit) ---
    if (gtag && (PIXEL_CONFIG.GOOGLE_ADS_ID || PIXEL_CONFIG.GOOGLE_ADS_ID_2) &&
        eventType === "form_submit") {
      gtag("event", "generate_lead", {});
    }
  } catch (e) {
    // never break the app because of a pixel
  }
};

export const getPixelStatus = () => [
  {
    name: "Google Analytics 4",
    envVar: "REACT_APP_GA4_MEASUREMENT_ID",
    configured: !!PIXEL_CONFIG.GA4_MEASUREMENT_ID,
    value: PIXEL_CONFIG.GA4_MEASUREMENT_ID,
  },
  {
    name: "Google Ads",
    envVar: "REACT_APP_GOOGLE_ADS_ID",
    configured: !!PIXEL_CONFIG.GOOGLE_ADS_ID,
    value: PIXEL_CONFIG.GOOGLE_ADS_ID,
  },
  {
    name: "Taboola",
    envVar: "REACT_APP_TABOOLA_PIXEL_ID",
    configured: !!PIXEL_CONFIG.TABOOLA_PIXEL_ID,
    value: PIXEL_CONFIG.TABOOLA_PIXEL_ID,
  },
  {
    name: "Whop",
    envVar: "REACT_APP_WHOP_COMPANY_ID",
    configured: !!PIXEL_CONFIG.WHOP_COMPANY_ID,
    value: PIXEL_CONFIG.WHOP_COMPANY_ID,
  },
];
